/**
 * Format a stored date without ever throwing.
 *
 * WHY. date-fns `format` throws RangeError("Invalid time value") on anything that is not a valid
 * Date, and a throw inside render blanks the whole component. The pattern
 * `format(value.toDate?.() || value, ...)` assumed every stored value is a Firestore Timestamp
 * instance; a Timestamp that lost its prototype (a plain {seconds, nanoseconds} object from a
 * cache or a callable reply) or an unparseable value reached `format` raw and crashed the Al chat
 * panel (Dev feed 2026-10-30, error group 32b4ebd7db8a3f86).
 */
import { format } from 'date-fns';
import { toDateSafe } from './timestamp';

/**
 * A Timestamp, plain {seconds} / {_seconds} object, Date, epoch ms or date string as a valid Date, or null.
 * A thin wrapper over the shared toDateSafe, which already reads every one of those shapes. The ONE difference kept on
 * purpose: falsy values (incl. 0 and "") are "no date" here, as every caller treated them before this helper existed,
 * whereas toDateSafe reads 0 as the epoch (1970-01-01).
 */
export function toValidDate(value: unknown): Date | null {
  return value ? toDateSafe(value) : null;
}

/** date-fns `format` for a stored value; returns `fallback` instead of throwing when the value is not a valid date. */
export function safeFormatDate(value: unknown, pattern: string, fallback = ''): string {
  const date = toValidDate(value);
  return date ? format(date, pattern) : fallback;
}
